import PropTypes from "prop-types"
import React, { useState } from "react"

import { sanitize } from "../../../utils/utils"
import { formatFontFamily, formatHeadingFontFamily } from "../utils/fontUtils"

const ServiceDetails = ({ service, theme }) => {
  const [showFullDescription, setShowFullDescription] = useState(false)

  const truncateDescription = (text, limit = 200) => {
    if (!text) return ""
    if (text.length <= limit) return text
    return text.substring(0, limit) + "..."
  }

  const displayDescription = showFullDescription ? service.description : truncateDescription(service.description, 200)

  return (
    <div className="flex w-full flex-col items-start justify-start gap-10">
      {/* Service Description */}
      {service.description && (
        <div className="flex w-full flex-col gap-4">
          <h3
            className="text-[24px] font-bold leading-[36px]"
            style={{
              fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
              color: theme?.colors?.textDark || "#55433f"
            }}>
            Details
          </h3>
          <div className="flex flex-col gap-4">
            <div
              className="text-[18px] font-normal leading-[32px]"
              style={{
                fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                color: theme?.colors?.textDark || "#55433f"
              }}
              dangerouslySetInnerHTML={{
                __html: sanitize(displayDescription)
              }}
            />
            {service.description.length > 200 && (
              <button
                onClick={() => setShowFullDescription(!showFullDescription)}
                className="text-left font-semibold underline decoration-solid transition-colors hover:no-underline"
                style={{
                  fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                  color: theme?.colors?.primary || "#bd654f"
                }}>
                {showFullDescription ? "Show less" : "Read more"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Service Features/Benefits */}
      {service.features && service.features.length > 0 && (
        <div className="flex w-full flex-col gap-4">
          <h3
            className="text-[24px] font-bold leading-[36px]"
            style={{
              fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
              color: theme?.colors?.textDark || "#55433f"
            }}>
            What&apos;s included
          </h3>
          <ul className="flex flex-col gap-2">
            {service.features.map((feature, index) => (
              <li key={index} className="flex items-start gap-3">
                <div
                  className="mt-2 h-2 w-2 rounded-full"
                  style={{ backgroundColor: theme?.colors?.primary || "#bd654f" }}
                />
                <span
                  className="text-[16px] font-normal leading-[24px]"
                  style={{
                    fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                    color: theme?.colors?.textDark || "#55433f"
                  }}>
                  {feature}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Service Policies */}
      {service.policies && service.policies.length > 0 && (
        <div className="flex w-full flex-col gap-4">
          <h3
            className="text-[24px] font-bold leading-[36px]"
            style={{
              fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
              color: theme?.colors?.textDark || "#55433f"
            }}>
            Important information
          </h3>
          <ul className="flex flex-col gap-2">
            {service.policies.map((policy, index) => (
              <li key={index} className="flex items-start gap-3">
                <div
                  className="mt-2 h-2 w-2 rounded-full"
                  style={{ backgroundColor: theme?.colors?.primary || "#bd654f" }}
                />
                <span
                  className="text-[16px] font-normal leading-[24px]"
                  style={{
                    fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                    color: theme?.colors?.textDark || "#55433f"
                  }}>
                  {policy}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

ServiceDetails.propTypes = {
  service: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    amountCents: PropTypes.number,
    timeLength: PropTypes.number,
    imageUrl: PropTypes.string,
    virtual: PropTypes.bool,
    atHome: PropTypes.bool,
    locations: PropTypes.arrayOf(
      PropTypes.shape({
        hide_address: PropTypes.bool
      })
    ),
    variations: PropTypes.arrayOf(PropTypes.object),
    features: PropTypes.arrayOf(PropTypes.string),
    policies: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  practice: PropTypes.object,
  theme: PropTypes.object
}

export default ServiceDetails
